import React, { useEffect, useState } from 'react';
import { DashboardLayout } from '../../layouts/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';
import { getClients, createClient, updateClient, deleteClient, getRegimenesFiscales, getUsosCFDI } from '../../services/api';
import { Plus, Search, Edit, Trash2, X, UserCheck, Users, Loader2, Receipt } from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '../../utils/errorUtils';

const emptyForm = {
  nombre: '',
  rfc: '',
  razon_social: '',
  regimen_fiscal: '601',
  uso_cfdi: 'G03',
  domicilio_fiscal: '',
  email: '',
  phone: '',
  notes: '',
  discount_default_percent: 0,
  pricing_profile: 'Normal'
};

const Clientes = () => {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [saving, setSaving] = useState(false);
  const [regimenes, setRegimenes] = useState([]);
  const [usosCFDI, setUsosCFDI] = useState([]);
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => {
    loadClients();
    loadCatalogs();
  }, [user]);

  const loadClients = async () => {
    if (!user?.business_id) return;
    try {
      const response = await getClients(user.business_id);
      setClients(response.data);
    } catch (error) {
      console.error('Error loading clients:', error);
      toast.error('Error al cargar clientes');
    } finally {
      setLoading(false);
    }
  };

  const loadCatalogs = async () => {
    try {
      const [regRes, usosRes] = await Promise.all([getRegimenesFiscales(), getUsosCFDI()]);
      setRegimenes(regRes.data);
      setUsosCFDI(usosRes.data);
    } catch (error) {
      console.error('Error loading SAT catalogs:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const clientData = {
        nombre: formData.nombre.trim() || null,
        rfc: formData.rfc.toUpperCase().trim(),
        razon_social: formData.razon_social.toUpperCase().trim(),
        regimen_fiscal: formData.regimen_fiscal,
        uso_cfdi: formData.uso_cfdi,
        domicilio_fiscal: formData.domicilio_fiscal.trim(),
        email: formData.email.trim() || null,
        phone: formData.phone.trim() || null,
        notes: formData.notes.trim() || null,
        business_id: user.business_id,
        discount_default_percent: parseFloat(formData.discount_default_percent) || 0,
        pricing_profile: formData.pricing_profile
      };

      if (editingClient) {
        const { business_id, ...updateData } = clientData;
        await updateClient(editingClient.id, updateData);
        toast.success('Cliente actualizado');
      } else {
        await createClient(clientData);
        toast.success('Cliente creado');
      }

      setShowModal(false);
      setEditingClient(null);
      setFormData(emptyForm);
      loadClients();
    } catch (error) {
      console.error('Error saving client:', error);
      toast.error(getErrorMessage(error, 'Error al guardar cliente'));
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({
      nombre: client.nombre || '',
      rfc: client.rfc || '',
      razon_social: client.razon_social || '',
      regimen_fiscal: client.regimen_fiscal || '601',
      uso_cfdi: client.uso_cfdi || 'G03',
      domicilio_fiscal: client.domicilio_fiscal || '',
      email: client.email || '',
      phone: client.phone || '',
      notes: client.notes || '',
      discount_default_percent: client.discount_default_percent || 0,
      pricing_profile: client.pricing_profile || 'Normal'
    });
    setShowModal(true);
  };

  const handleDelete = async (clientId) => {
    if (!window.confirm('¿Estás seguro de eliminar este cliente?')) return;
    try {
      await deleteClient(clientId);
      toast.success('Cliente eliminado');
      loadClients();
    } catch (error) {
      console.error('Error deleting client:', error);
      toast.error('Error al eliminar cliente');
    }
  };

  const getRegimenName = (code) => {
    const r = regimenes.find(r => r.Value === code);
    return r ? r.Name : code;
  };

  const filteredClients = clients.filter((client) => {
    const term = searchTerm.toLowerCase();
    return (
      (client.nombre && client.nombre.toLowerCase().includes(term)) ||
      (client.razon_social && client.razon_social.toLowerCase().includes(term)) ||
      (client.rfc && client.rfc.toLowerCase().includes(term)) ||
      (client.email && client.email.toLowerCase().includes(term)) ||
      (client.phone && client.phone.includes(term))
    );
  });

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-700"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 tracking-tight">Clientes</h1>
            <p className="text-slate-600 mt-2">Gestiona tus clientes para facturación</p>
          </div>
          <button
            onClick={() => { setEditingClient(null); setFormData(emptyForm); setShowModal(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-teal-700 text-white rounded-lg hover:bg-teal-800 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Nuevo Cliente
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-teal-50 rounded-lg"><Users className="w-5 h-5 text-teal-600" /></div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{clients.length}</p>
                <p className="text-sm text-slate-500">Total clientes</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-50 rounded-lg"><UserCheck className="w-5 h-5 text-green-600" /></div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{clients.filter(c => c.email).length}</p>
                <p className="text-sm text-slate-500">Con email</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 rounded-lg"><Receipt className="w-5 h-5 text-blue-600" /></div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{clients.filter(c => c.rfc && c.rfc !== 'XAXX010101000').length}</p>
                <p className="text-sm text-slate-500">Con RFC propio</p>
              </div>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            placeholder="Buscar por nombre, RFC, razón social, email o teléfono..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
          />
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {filteredClients.length === 0 ? (
            <div className="p-12 text-center">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">
                {searchTerm ? 'No se encontraron clientes' : 'No tienes clientes registrados'}
              </p>
              <p className="text-slate-400 text-sm mt-1">
                {searchTerm ? 'Intenta con otro término de búsqueda' : 'Agrega tu primer cliente para comenzar'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Cliente</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">RFC</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider hidden lg:table-cell">Régimen Fiscal</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider hidden md:table-cell">C.P. Fiscal</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider hidden lg:table-cell">Contacto</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredClients.map((client) => (
                    <tr key={client.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-6 py-4">
                        {client.nombre && (
                          <p className="font-medium text-slate-900">{client.nombre}</p>
                        )}
                        <p className={client.nombre ? 'text-sm text-slate-500' : 'font-medium text-slate-900'}>
                          {client.razon_social}
                        </p>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="font-mono text-sm text-slate-900">{client.rfc || '—'}</span>
                      </td>
                      <td className="px-6 py-4 hidden lg:table-cell">
                        {client.regimen_fiscal ? (
                          <span className="text-sm text-slate-600">{client.regimen_fiscal} - {getRegimenName(client.regimen_fiscal)}</span>
                        ) : (
                          <span className="text-sm text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap hidden md:table-cell">
                        <span className="font-mono text-sm text-slate-600">{client.domicilio_fiscal || '—'}</span>
                      </td>
                      <td className="px-6 py-4 hidden lg:table-cell">
                        <div className="text-sm text-slate-600 space-y-0.5">
                          {client.email && <p>{client.email}</p>}
                          {client.phone && <p>{client.phone}</p>}
                          {!client.email && !client.phone && <span className="text-slate-400">—</span>}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleEdit(client)}
                            className="p-1.5 text-slate-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
                            title="Editar"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(client.id)}
                            className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Eliminar"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between rounded-t-xl z-10">
              <h2 className="text-xl font-bold text-slate-900">
                {editingClient ? 'Editar Cliente' : 'Nuevo Cliente'}
              </h2>
              <button
                onClick={() => { setShowModal(false); setEditingClient(null); setFormData(emptyForm); }}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">

              {/* Sección: Datos de contacto */}
              <div>
                <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">
                  Datos de contacto
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-1">Nombre del cliente</label>
                    <input
                      type="text"
                      value={formData.nombre}
                      onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="Nombre o alias como aparece en caja"
                    />
                    <p className="text-xs text-slate-500 mt-1">Nombre de contacto o alias usado en el POS (opcional)</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="cliente@ejemplo.com"
                    />
                    <p className="text-xs text-slate-500 mt-1">Se usará para enviar facturas</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Teléfono</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="(000) 000-0000"
                    />
                  </div>
                </div>
              </div>

              {/* Sección: Datos fiscales */}
              <div>
                <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">
                  Datos fiscales
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">RFC *</label>
                    <input
                      type="text"
                      value={formData.rfc}
                      onChange={(e) => setFormData({ ...formData, rfc: e.target.value.toUpperCase() })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent font-mono uppercase"
                      placeholder="XAXX010101000"
                      maxLength={13}
                      minLength={12}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Razón Social *</label>
                    <input
                      type="text"
                      value={formData.razon_social}
                      onChange={(e) => setFormData({ ...formData, razon_social: e.target.value.toUpperCase() })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent uppercase"
                      placeholder="NOMBRE O RAZÓN SOCIAL FISCAL"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Régimen Fiscal *</label>
                    <select
                      value={formData.regimen_fiscal}
                      onChange={(e) => setFormData({ ...formData, regimen_fiscal: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      required
                    >
                      {regimenes.length > 0 ? (
                        regimenes.map(r => (
                          <option key={r.Value} value={r.Value}>{r.Value} - {r.Name}</option>
                        ))
                      ) : (
                        <>
                          <option value="601">601 - General de Ley Personas Morales</option>
                          <option value="612">612 - Personas Físicas con Actividades Empresariales</option>
                          <option value="616">616 - Sin obligaciones fiscales</option>
                          <option value="621">621 - Incorporación Fiscal</option>
                          <option value="626">626 - Régimen Simplificado de Confianza</option>
                        </>
                      )}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Uso CFDI *</label>
                    <select
                      value={formData.uso_cfdi}
                      onChange={(e) => setFormData({ ...formData, uso_cfdi: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      required
                    >
                      {usosCFDI.length > 0 ? (
                        usosCFDI.map(u => (
                          <option key={u.Value} value={u.Value}>{u.Value} - {u.Name}</option>
                        ))
                      ) : (
                        <>
                          <option value="G03">G03 - Gastos en general</option>
                          <option value="G01">G01 - Adquisición de mercancías</option>
                          <option value="G02">G02 - Devoluciones, descuentos o bonificaciones</option>
                          <option value="S01">S01 - Sin efectos fiscales</option>
                        </>
                      )}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Código Postal Fiscal *</label>
                    <input
                      type="text"
                      value={formData.domicilio_fiscal}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '');
                        setFormData({ ...formData, domicilio_fiscal: val });
                      }}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent font-mono"
                      placeholder="00000"
                      maxLength={5}
                      pattern="[0-9]{5}"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Sección: Comercial */}
              <div>
                <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">
                  Condiciones comerciales
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Descuento por defecto (%)</label>
                    <input
                      type="number"
                      value={formData.discount_default_percent}
                      onChange={(e) => setFormData({ ...formData, discount_default_percent: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      placeholder="0"
                      min="0"
                      max="100"
                      step="0.01"
                    />
                    <p className="text-xs text-slate-500 mt-1">Se aplica automáticamente en cotizaciones</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Perfil de precios</label>
                    <select
                      value={formData.pricing_profile}
                      onChange={(e) => setFormData({ ...formData, pricing_profile: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    >
                      <option value="Normal">Normal</option>
                      <option value="Mayoreo">Mayoreo</option>
                      <option value="Distribuidor">Distribuidor</option>
                      <option value="VIP">VIP</option>
                    </select>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-1">Notas</label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      rows={3}
                      placeholder="Notas internas sobre este cliente..."
                    />
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-3 pt-2 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); setEditingClient(null); setFormData(emptyForm); }}
                  className="px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-teal-700 text-white rounded-lg hover:bg-teal-800 transition-colors disabled:opacity-50"
                >
                  {saving ? (
                    <><Loader2 className="w-4 h-4 animate-spin" />Guardando...</>
                  ) : (
                    editingClient ? 'Actualizar' : 'Guardar'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default Clientes;
